import { useQuery } from "@apollo/client"
import { useEffect, useState } from "react"
import { gql } from "~/__generated__"
import { InvitationStatus } from "~/__generated__/graphql"
import { cn } from "~/common/cn"
import { useSafeMutation } from "~/common/use-safe-mutation"
import actionsIcon from "~/images/icons/actions"
import calendarClockIcon from "~/images/icons/calendar-clock"
import infoIcon from "~/images/icons/info"
import link2Icon from "~/images/icons/link-2"
import loaderIcon from "~/images/icons/loader-icon"
import tableIcon from "~/images/icons/table"
import tagIcon from "~/images/icons/tag"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/ui/alert-dialog"
import { Button } from "~/ui/button"
import { GraphqlError } from "~/ui/errors"
import {
  MobileCardContent,
  MobileCardHeader,
  MobileCardRow,
  ResponsiveTable,
  ResponsiveTableBody,
  ResponsiveTableCell,
  ResponsiveTableHeader,
  ResponsiveTableHeaderCell,
  ResponsiveTableRow,
} from "~/ui/responsive-table"
import { useToast } from "~/ui/use-toast"
import { TagFilter } from "~/ui/tag-filter"
import { HeaderWithTableFilters } from "~/ui/header-with-table-filters"
import { FilteredEmptyState } from "~/ui/filtered-empty-state"
import { TableTabs } from "~/ui/table-tabs"
import { PostalGradientBar } from "~/ui/postal-gradient-bar"
import { InvitationCreateDialog } from "./invitation-create-dialog"
import { InvitationTagCell } from "./invitation-tag-cell"

const query = gql(/* GraphQL */ `
  query GetInvitations($first: Int, $after: String, $filters: InvitationFiltersInput) {
    viewer {
      tags {
        id
        name
      }
      invitations(first: $first, after: $after, filters: $filters) {
        edges {
          node {
            id
            title
            token
            status
            contactsAddedCount
            createdAt
            updatedAt
            inviteUrl
            tags {
              id
              name
            }
          }
        }
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
      }
    }
  }
`)

export const updateInvitationMutation = gql(/* GraphQL */ `
  mutation UpdateInvitation($input: InvitationUpdateInput!) {
    invitationUpdate(input: $input) {
      invitation {
        id
        status
      }
    }
  }
`)

export function InvitationsScreen() {
  const { toast } = useToast()
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [expireDialogOpen, setExpireDialogOpen] = useState(false)
  const [invitationToExpire, setInvitationToExpire] = useState<{
    id: string
    title: string
  } | null>(null)
  const { data, previousData, loading, error, fetchMore, refetch } = useQuery(query, {
    variables: {
      first: 50,
      after: null,
      filters: selectedTags.length > 0 ? { tags: selectedTags } : null,
    },
    notifyOnNetworkStatusChange: true,
  })

  const [updateInvitation] = useSafeMutation(updateInvitationMutation, {
    onCompleted: () => {
      toast({
        title: "Success",
        description: "Invitation has been expired.",
      })
      refetch()
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to expire invitation.",
        variant: "destructive",
      })
    },
  })

  // Refetch when selectedTags changes
  useEffect(() => {
    refetch({
      first: 50,
      after: null,
      filters: selectedTags.length > 0 ? { tags: selectedTags } : null,
    })
  }, [selectedTags, refetch])

  const handleCreateInvite = () => {
    setShowCreateDialog(true)
  }

  const handleCreateSuccess = () => {
    toast({
      title: "Success",
      description: "Invitation created and link copied to clipboard!",
    })
  }

  const handleExpireLink = (invitation: { id: string; title: string }) => {
    setInvitationToExpire(invitation)
    setExpireDialogOpen(true)
  }

  const confirmExpireLink = async () => {
    if (!invitationToExpire) return

    try {
      await updateInvitation({
        variables: {
          input: {
            id: invitationToExpire.id,
            invitationInput: {
              status: InvitationStatus.Expired,
            },
          },
        },
      })
    } catch {
      // Error handling is done in the mutation's onError callback
    } finally {
      setExpireDialogOpen(false)
      setInvitationToExpire(null)
    }
  }

  const handleCopyLink = async (inviteUrl: string) => {
    try {
      await navigator.clipboard.writeText(inviteUrl)
      toast({
        title: "Link copied",
        description: "Invitation link has been copied to your clipboard.",
      })
    } catch {
      toast({
        title: "Error",
        description: "Failed to copy link to clipboard.",
        variant: "destructive",
      })
    }
  }

  const invitations = (data ?? previousData)?.viewer?.invitations?.edges || []
  const allTags = (data ?? previousData)?.viewer?.tags || []

  if (error) {
    return <GraphqlError error={error} />
  }

  return (
    <div>
      <PostalGradientBar />
      <div className="px-4 py-8">
        <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <HeaderWithTableFilters
            title="Invite Links"
            subtitle="Manage your invitations"
            selectedTags={selectedTags}
            allTags={allTags}
            onTagRemove={(tagId) => setSelectedTags(selectedTags.filter((id) => id !== tagId))}
            onClearAll={() => setSelectedTags([])}
          />
          <Button onClick={handleCreateInvite}>Create an Invite</Button>
        </div>

        <div className="border-b">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <TableTabs
              tabs={[{ id: "all", label: "All Invites", icon: tableIcon }]}
              activeTab="all"
              onTabChange={() => {}} // Only one tab, no-op
            />
            <div className="flex items-center gap-4">
              <TagFilter
                tags={allTags}
                selectedTags={selectedTags}
                onTagsChange={setSelectedTags}
              />
            </div>
          </div>
        </div>

        {loading && !data ? (
          <div className="flex justify-center py-8">
            <img {...loaderIcon} alt="Loading..." className="animate-spin" />
          </div>
        ) : invitations.length === 0 ? (
          <FilteredEmptyState
            selectedTags={selectedTags}
            allTags={allTags}
            onClearFilters={() => setSelectedTags([])}
            itemType="invitations"
            emptyTitle="No Invitations"
            emptyDescription="Create an invite to share with your network and add them to your contacts!"
            emptyAction={<Button onClick={handleCreateInvite}>Create an Invite</Button>}
            filteredAction={
              <Button variant="outline" onClick={handleCreateInvite}>
                Create an Invite
              </Button>
            }
          />
        ) : (
          <ResponsiveTable className="mt-4 md:mt-0">
            <ResponsiveTableHeader>
              <ResponsiveTableHeaderCell span={4}>
                <div className="flex items-center gap-2">
                  <img src={link2Icon.src} alt="" className="h-4 w-4" />
                  Invite Link
                </div>
              </ResponsiveTableHeaderCell>
              <ResponsiveTableHeaderCell span={2}>
                <div className="flex items-center gap-2">
                  <img src={tagIcon.src} alt="" className="h-4 w-4" />
                  Tags
                </div>
              </ResponsiveTableHeaderCell>
              <ResponsiveTableHeaderCell span={2}>
                <div className="flex items-center gap-2">
                  <img src={infoIcon.src} alt="" className="h-4 w-4" />
                  Contacts Added
                </div>
              </ResponsiveTableHeaderCell>
              <ResponsiveTableHeaderCell span={3}>
                <div className="flex items-center gap-2">
                  <img src={calendarClockIcon.src} alt="" className="h-4 w-4" />
                  Date Created
                </div>
              </ResponsiveTableHeaderCell>
              <ResponsiveTableHeaderCell span={1}>
                <div className="flex items-center gap-2">
                  <img src={actionsIcon.src} alt="" className="h-4 w-4" />
                  Actions
                </div>
              </ResponsiveTableHeaderCell>
            </ResponsiveTableHeader>

            <ResponsiveTableBody>
              {invitations.map(({ node: invitation }) => {
                if (!invitation) return null
                return (
                  <ResponsiveTableRow
                    key={invitation.id}
                    desktopContent={
                      <>
                        <ResponsiveTableCell
                          span={4}
                          className={cn(
                            "gap-3",
                            invitation.status === InvitationStatus.Expired ? "opacity-60" : ""
                          )}
                        >
                          <div className="flex min-w-0 flex-1 flex-col">
                            <div className="flex items-center gap-2">
                              <div className="truncate font-medium">{invitation.title}</div>
                            </div>
                            <div className="truncate text-sm text-muted-foreground">
                              {invitation.inviteUrl}
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 px-2 text-xs"
                            onClick={() => handleCopyLink(invitation.inviteUrl)}
                            disabled={invitation.status === InvitationStatus.Expired}
                          >
                            Copy
                          </Button>
                        </ResponsiveTableCell>

                        <ResponsiveTableCell span={2}>
                          <InvitationTagCell tags={invitation.tags} />
                        </ResponsiveTableCell>

                        <ResponsiveTableCell span={2}>
                          <span className="font-medium">{invitation.contactsAddedCount}</span>
                        </ResponsiveTableCell>

                        <ResponsiveTableCell span={3}>
                          <div>
                            <div className="text-sm">
                              {new Date(invitation.createdAt).toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              })}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {new Date(invitation.createdAt).toLocaleTimeString("en-US", {
                                hour: "numeric",
                                minute: "2-digit",
                                hour12: true,
                                timeZoneName: "short",
                              })}
                            </div>
                          </div>
                        </ResponsiveTableCell>

                        <ResponsiveTableCell span={1} className="justify-end">
                          {invitation.status === InvitationStatus.Expired ? (
                            <Button variant="outline" size="sm" disabled>
                              Expired
                            </Button>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                handleExpireLink({ id: invitation.id, title: invitation.title })
                              }
                            >
                              Expire Link
                            </Button>
                          )}
                        </ResponsiveTableCell>
                      </>
                    }
                    mobileContent={
                      <>
                        <MobileCardHeader>
                          <div
                            className={cn(
                              "-m-2 flex min-w-0 flex-1 items-center gap-3 rounded p-2",
                              invitation.status === InvitationStatus.Expired ? "opacity-60" : ""
                            )}
                          >
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <div className="truncate font-medium">{invitation.title}</div>
                                {invitation.status === "EXPIRED" && (
                                  <span className="inline-flex items-center rounded-md bg-red-50 px-2 py-1 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-600/20">
                                    Expired
                                  </span>
                                )}
                              </div>
                              <div className="truncate text-sm text-muted-foreground">
                                {invitation.inviteUrl}
                              </div>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 px-2 text-xs"
                              onClick={() => handleCopyLink(invitation.inviteUrl)}
                              disabled={invitation.status === InvitationStatus.Expired}
                            >
                              Copy
                            </Button>
                          </div>
                          {invitation.status === InvitationStatus.Expired ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="ml-2 shrink-0 text-muted-foreground"
                              disabled
                            >
                              Expired
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="ml-2 shrink-0 text-muted-foreground"
                              onClick={() =>
                                handleExpireLink({ id: invitation.id, title: invitation.title })
                              }
                            >
                              Expire
                            </Button>
                          )}
                        </MobileCardHeader>

                        <MobileCardContent>
                          <MobileCardRow label="Tags">
                            <InvitationTagCell tags={invitation.tags} />
                          </MobileCardRow>

                          <MobileCardRow label="Contacts Added">
                            <span className="font-medium">{invitation.contactsAddedCount}</span>
                          </MobileCardRow>

                          <MobileCardRow label="Date Created">
                            <div className="text-right">
                              <div className="text-sm">
                                {new Date(invitation.createdAt).toLocaleDateString("en-US", {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                })}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {new Date(invitation.createdAt).toLocaleTimeString("en-US", {
                                  hour: "numeric",
                                  minute: "2-digit",
                                  hour12: true,
                                })}
                              </div>
                            </div>
                          </MobileCardRow>
                        </MobileCardContent>
                      </>
                    }
                  />
                )
              })}
            </ResponsiveTableBody>
          </ResponsiveTable>
        )}

        {/* Load More / Pagination */}
        {data?.viewer?.invitations?.pageInfo.hasNextPage && (
          <div className="mt-4 flex justify-center">
            <Button
              variant="outline"
              onClick={() => {
                fetchMore({
                  variables: {
                    after: data?.viewer?.invitations?.pageInfo.endCursor,
                  },
                })
              }}
            >
              Load more
            </Button>
          </div>
        )}

        <InvitationCreateDialog
          open={showCreateDialog}
          onOpenChange={setShowCreateDialog}
          onSuccess={handleCreateSuccess}
        />

        <AlertDialog open={expireDialogOpen} onOpenChange={setExpireDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you sure you would like to expire this link?</AlertDialogTitle>
              <AlertDialogDescription>
                No one will be able to use this link to join your contacts.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={confirmExpireLink}>Expire</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  )
}
